/**
 * DragHandleWrapper Component
 * Notion-style block handles with + button and drag grip
 * 
 * Features:
 * - Plus button for inserting new blocks
 * - Drag grip for opening block menu and drag-reordering
 * - Global keyboard shortcuts for moving nodes (Cmd/Ctrl+Shift+↑/↓)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import BlockMenu from './BlockMenu';

interface DragHandleWrapperProps {
    editor: Editor | null;
}

/**
 * Check if a node can be moved in the specified direction
 */
const canMoveNode = (editor: Editor | null, direction: 'up' | 'down'): boolean => {
    if (!editor) return false;

    const { state } = editor;
    const { selection } = state;
    const { $from } = selection;

    const depth = $from.depth;
    if (depth < 1) return false;

    const parentNode = $from.node(depth - 1);
    const indexInParent = $from.index(depth - 1);

    if (direction === 'up') {
        return indexInParent > 0;
    } else {
        return indexInParent < parentNode.childCount - 1;
    }
};

/**
 * Move the selected node in the specified direction
 */
const moveNode = (editor: Editor | null, direction: 'up' | 'down'): boolean => {
    if (!editor) return false;
    if (!canMoveNode(editor, direction)) return false;

    const { state, view } = editor;
    const { tr } = state;
    const { selection } = state;
    const { $from } = selection;

    try {
        const depth = $from.depth;
        const parentNode = $from.node(depth - 1);
        const indexInParent = $from.index(depth - 1);

        const nodeStart = $from.before(depth);
        const nodeEnd = $from.after(depth);
        const nodeContent = state.doc.slice(nodeStart, nodeEnd);

        if (direction === 'up' && indexInParent > 0) {
            let offset = 0;
            for (let i = 0; i < indexInParent - 1; i++) {
                offset += parentNode.child(i).nodeSize;
            }
            const prevNodeStart = $from.start(depth - 1) + offset;

            tr.delete(nodeStart, nodeEnd);
            tr.insert(prevNodeStart, nodeContent.content);
            view.dispatch(tr);
            editor.commands.setTextSelection(prevNodeStart + 1);
            return true;
        } else if (direction === 'down' && indexInParent < parentNode.childCount - 1) {
            const nextNodeStart = nodeEnd;
            const nextNodeEnd = nextNodeStart + parentNode.child(indexInParent + 1).nodeSize;
            const nextContent = state.doc.slice(nextNodeStart, nextNodeEnd);

            tr.delete(nextNodeStart, nextNodeEnd);
            tr.insert(nodeStart, nextContent.content);
            view.dispatch(tr);
            editor.commands.setTextSelection(nodeStart + nextContent.content.size + 1);
            return true;
        }

        return false;
    } catch (e) {
        console.error('Error moving node:', e);
        return false;
    }
};

const DragHandleWrapper: React.FC<DragHandleWrapperProps> = ({ editor }) => {
    const [showBlockMenu, setShowBlockMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [handlePosition, setHandlePosition] = useState({ top: 0, show: false });
    const [currentNodeType, setCurrentNodeType] = useState('Paragraph');
    const containerRef = useRef<HTMLDivElement>(null);

    // Global keyboard shortcuts for moving nodes
    useEffect(() => {
        if (!editor) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Only handle when editor is focused
            if (!editor.isFocused) return;

            // Cmd/Ctrl + Shift + Arrow Up/Down
            if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
                if (e.key === 'ArrowUp') {
                    if (canMoveNode(editor, 'up')) {
                        e.preventDefault();
                        moveNode(editor, 'up');
                    }
                } else if (e.key === 'ArrowDown') {
                    if (canMoveNode(editor, 'down')) {
                        e.preventDefault();
                        moveNode(editor, 'down');
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [editor]);

    // Update handle position based on cursor
    useEffect(() => {
        if (!editor) return;

        const updatePosition = () => {
            const { state } = editor;
            const { selection } = state;
            const { $from } = selection;

            // Get the DOM node for the current block
            const pos = $from.before($from.depth + 1);
            const coords = editor.view.coordsAtPos(pos);
            const editorRect = editor.view.dom.getBoundingClientRect();

            // Get the actual DOM element to calculate its height for vertical centering
            const domNode = editor.view.domAtPos(pos + 1);
            let lineHeight = 24; // Default line height
            if (domNode && domNode.node) {
                const element = domNode.node.nodeType === Node.TEXT_NODE
                    ? domNode.node.parentElement
                    : domNode.node as HTMLElement;
                if (element) {
                    const style = window.getComputedStyle(element);
                    const computedLineHeight = parseFloat(style.lineHeight);
                    if (!isNaN(computedLineHeight)) {
                        lineHeight = computedLineHeight;
                    } else {
                        // If line-height is 'normal', estimate based on font size
                        const fontSize = parseFloat(style.fontSize);
                        lineHeight = fontSize * 1.5;
                    }
                }
            }

            // Calculate vertical offset to center the handles with the first line of text
            // Handle height is 24px, so offset by (lineHeight - 24) / 2 to center
            const verticalOffset = (lineHeight - 24) / 2;

            // Determine current node type
            const node = $from.node($from.depth);
            let nodeName = 'Paragraph';
            if (node) {
                if (node.type.name === 'heading') {
                    nodeName = `Heading ${node.attrs.level}`;
                } else if (node.type.name === 'bulletList') {
                    nodeName = 'Bullet List';
                } else if (node.type.name === 'orderedList') {
                    nodeName = 'Numbered List';
                } else if (node.type.name === 'blockquote') {
                    nodeName = 'Blockquote';
                } else if (node.type.name === 'codeBlock') {
                    nodeName = 'Code Block';
                } else {
                    nodeName = node.type.name.charAt(0).toUpperCase() + node.type.name.slice(1);
                }
            }

            setCurrentNodeType(nodeName);
            setHandlePosition({
                top: coords.top - editorRect.top,
                show: true,
            });
        };


        editor.on('selectionUpdate', updatePosition);
        editor.on('focus', () => {
            updatePosition(); // Update position when focusing
        });
        editor.on('blur', () => {
            // Delay to allow menu interactions
            setTimeout(() => {
                if (!showBlockMenu) {
                    setHandlePosition(prev => ({ ...prev, show: false }));
                }
            }, 200);
        });

        // Initialize position if editor is already focused
        if (editor.isFocused) {
            updatePosition();
        }

        return () => {
            editor.off('selectionUpdate', updatePosition);
        };
    }, [editor, showBlockMenu]);

    const handleGripClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuPosition({ x: e.clientX, y: e.clientY });
        setShowBlockMenu(true);
    }, []);

    const handlePlusClick = useCallback(() => {
        if (!editor) return;
        // Insert a new paragraph and trigger slash command
        editor.chain().focus().insertContent('/').run();
    }, [editor]);

    if (!editor) return null;

    return (
        <>
            {/* Block handles - positioned inside the editor's left padding area */}
            <div
                ref={containerRef}
                className="drag-handle-wrapper"
                style={{
                    position: 'absolute',
                    left: '68px',
                    top: `${handlePosition.top}px`,
                    opacity: handlePosition.show ? 1 : 0,
                    pointerEvents: handlePosition.show ? 'auto' : 'none',
                    transition: 'opacity 0.15s ease, top 0.1s ease',
                }}
            >
                <div className="drag-handles">
                    {/* Plus button */}
                    <button
                        className="handle-btn plus-btn"
                        onClick={handlePlusClick}
                        title="Click for options"
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>

                    {/* Drag grip */}
                    <button
                        className="handle-btn grip-btn"
                        onClick={handleGripClick}
                        title="Click for options\nHold for drag"
                        draggable
                        onDragStart={(e) => {
                            if (!editor) return;
                            const { state } = editor;
                            const { selection } = state;
                            const { $from } = selection;
                            const node = $from.node($from.depth);

                            if (node) {
                                const text = node.textContent || "";
                                // Calculate node position
                                const startPos = $from.before($from.depth);
                                const endPos = $from.after($from.depth);

                                const dragData = {
                                    text: text,
                                    id: Date.now().toString(),
                                    from: startPos,
                                    to: endPos,
                                    origin: 'editor-internal'
                                };

                                e.dataTransfer.setData("text/plain", text);
                                e.dataTransfer.setData("application/x-lawhelper-highlight", JSON.stringify(dragData));
                                e.dataTransfer.effectAllowed = 'copy';
                            }
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <circle cx="3.5" cy="2.5" r="1.2" fill="currentColor" />
                            <circle cx="8.5" cy="2.5" r="1.2" fill="currentColor" />
                            <circle cx="3.5" cy="6" r="1.2" fill="currentColor" />
                            <circle cx="8.5" cy="6" r="1.2" fill="currentColor" />
                            <circle cx="3.5" cy="9.5" r="1.2" fill="currentColor" />
                            <circle cx="8.5" cy="9.5" r="1.2" fill="currentColor" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Block context menu */}
            <BlockMenu
                editor={editor}
                isOpen={showBlockMenu}
                position={menuPosition}
                onClose={() => setShowBlockMenu(false)}
                currentNodeType={currentNodeType}
            />

            <style>{`
        .drag-handle-wrapper {
          z-index: 50;
        }

        .drag-handles {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .handle-btn {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          color: #9ca3af;
          transition: all 0.15s ease;
        }

        .handle-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .grip-btn {
          cursor: grab;
        }

        .grip-btn:active {
          cursor: grabbing;
        }

        /* Tooltip styling */
        .handle-btn[title] {
          position: relative;
        }
      `}</style>
        </>
    );
};

export default DragHandleWrapper;
